import React, { useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Icon from '../Icon';
import SiteContentProvider, { useSiteContent } from '../SiteContent';
import T from '../components/EditableText';
import EditBar from '../components/EditBar';
import { SITES } from '../sites';
import useSiteIcons from '../useSiteIcons';
import useUmamiPageview from '../useUmamiPageview';
import { track, M38_EVENTS } from './tracking';
import '../website.css';
import './m38.css';

/**
 * Rahmen der Kampagnen-Landingpage.
 *
 * Die Wurzel trägt `site site--m38`: derselbe Auftritt wie die Kundenwebsite,
 * nur in einer Variante. Damit erbt die Seite Palette, Schriften, Raster und
 * die Einbettung in die App-Shell — siehe `m38.css`.
 *
 * Bewusst **nicht** `SiteLayout`: Die Landingpage hat keine Navigation. Das ist
 * keine Sparmaßnahme, sondern der Kern des Konzepts — wer über einen
 * Radiobeitrag herkommt, soll genau einen Weg vorfinden (Gespräch vereinbaren)
 * und nicht in Hauswirtschaft, Stellenangeboten oder Beiträgen versanden.
 *
 * Der [SiteContentProvider] ist derselbe wie beim Hauptauftritt: Die Texte
 * beider Auftritte liegen in einer Tabelle, und der Kunde bearbeitet sie mit
 * demselben Stift an Ort und Stelle.
 */
const M38Layout: React.FC = () => (
  <SiteContentProvider>
    <M38Frame />
  </SiteContentProvider>
);

/** Die Rufnummer der Kampagne, samt wählbarer Fassung. */
export const useKampagnenTelefon = (): { label: string; href: string } => {
  const { t } = useSiteContent();
  const label = t('m38.telefon');
  // Aus der angezeigten Nummer die wählbare ableiten, damit beides nicht
  // auseinanderlaufen kann: alles außer Ziffern weg, führende 0 → +49.
  const ziffern = label.replace(/[^\d+]/g, '');
  const href = `tel:${ziffern.startsWith('0') ? `+49${ziffern.slice(1)}` : ziffern}`;
  return { label, href };
};

const M38Frame: React.FC = () => {
  const { pathname, hash } = useLocation();
  const telefon = useKampagnenTelefon();

  useSiteIcons();
  useUmamiPageview();

  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <div className="site site--m38">
      <a className="skip-link" href="#inhalt">
        Zum Inhalt springen
      </a>

      {/*
        Der Streifen sieht aus wie ein Hinweis, liest sich aber wie ein
        Verweis („unsere Beiträge zum Nachhören"). Also ist er einer — sonst
        tippt man darauf und es passiert nichts.
      */}
      <div className="topbar">
        <Link
          className="shell topbar-link"
          to="/#beitraege"
          onClick={() => track(M38_EVENTS.cta, { stelle: 'topbar' })}
        >
          <T k="m38.topbar" />
          <Icon name="arrow-right" size={15} />
        </Link>
      </div>

      <header className="m38-header">
        <div className="shell m38-nav">
          <Link className="brand" to="/" aria-label="Miteinander38 — zur Startseite">
            <span className="brandmark" aria-hidden="true">
              38
            </span>
            <span className="brandtext">
              <strong>
                <T k="m38.marke" />
              </strong>
              <span>
                <T k="m38.marke.zusatz" />
              </span>
            </span>
          </Link>
          <div className="nav-actions">
            <a
              className="nav-phone"
              href={telefon.href}
              onClick={() => track(M38_EVENTS.telefon, { stelle: 'kopfzeile' })}
            >
              {telefon.label}
            </a>
            <Link
              className="btn btn--primary btn--sm"
              to="/#betreuungscheck"
              onClick={() => track(M38_EVENTS.cta, { stelle: 'kopfzeile' })}
            >
              <T k="m38.abschluss.cta" />
            </Link>
          </div>
        </div>
      </header>

      <main id="inhalt">
        <Outlet />
      </main>

      <M38Footer />

      <div className="mobile-bar">
        <div className="mobile-bar-inner">
          <a
            className="btn btn--ghost"
            href={telefon.href}
            onClick={() => track(M38_EVENTS.telefon, { stelle: 'leiste' })}
          >
            <Icon name="phone" size={17} /> Anrufen
          </a>
          <Link
            className="btn btn--primary"
            to="/#betreuungscheck"
            onClick={() => track(M38_EVENTS.cta, { stelle: 'leiste' })}
          >
            Beratung anfragen
          </Link>
        </div>
      </div>

      <EditBar />
    </div>
  );
};

const M38Footer: React.FC = () => {
  const { t } = useSiteContent();

  return (
    <footer className="m38-footer">
      <div className="shell">
        {/*
          Marke und Wege oben, Kleingedrucktes darunter. Der Firmenname stand
          hier früher zusätzlich als fette Zeile — direkt über einer Wortmarke,
          die dasselbe sagt. Er lebt jetzt als Alternativtext des Logos weiter,
          damit der bearbeitbare Schlüssel einen Abnehmer behält.
        */}
        <div className="footer-top">
          <a
            className="footer-logo"
            href={SITES.antczak.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/website/img/logo.png"
              alt={t('kontaktdaten.firma')}
              width={572}
              height={240}
            />
          </a>
          <div className="footer-links">
            {/*
              Impressum und Datenschutz liegen unter dieser Domain und nicht als
              Verweis auf sb-antczak.de: §5 DDG verlangt sie „leicht erkennbar,
              unmittelbar erreichbar" auf dem Angebot selbst. Ein Link auf eine
              fremde Domain erfüllt das nicht zuverlässig.
            */}
            <Link to="/impressum">Impressum</Link>
            <Link to="/datenschutz">Datenschutz</Link>
            <a href={SITES.antczak.url} target="_blank" rel="noopener noreferrer">
              <T k="m38.footer.unternehmen" />
            </a>
          </div>
        </div>

        <div className="footer-meta">
          <span>
            {t('kontaktdaten.strasse')} · {t('kontaktdaten.plz')} {t('kontaktdaten.ort')}
          </span>
          <span>
            <T k="m38.footer.zusatz" />
          </span>
        </div>
      </div>
    </footer>
  );
};

export default M38Layout;
